/**
 * The party's words (ADR-101 decisions 4, 5, 9, 14, 19 and 23, and its note of 2026-10-15). Every sentence here is a
 * draft for the author's look at `/ui` (ADR-061's amendment "the export is a visual reference"): the invitation prompt
 * and the frames follow the design's `Invite` and `Party` cards, the notices and refusals are system lines in the chat
 * log. A character's name is handed in as the text the page prints (`{name}`, first and family names), a count as
 * text (`{count}`, `{max}`, `{seconds}`), and a wait through `{time}` as chat's mute times word it.
 *
 * `prompt.switchAccept`, `needFlag`, `needUnflag`, `unknown`, `switchCancelled`, `switchNotSent`, `switchEnded`, `switchStopped`, `unsafe` and `level` are drafts (`needFlag` and `needUnflag` are the author's words of 2026-10-16) (ADR-076's amendment of 2026-10-08, E3): the prompt's
 * flag sentences, the Accept that switches first, and the Flagged party's level gate. `{seconds}` is the switch's delay.
 *
 * `refused.combat` serves a trade or a duel when they ask the same rule (ADR-101's note of 2026-10-15).
 */
export const PARTY = {
  prompt: {
    title: "Party invitation",
    label: "Party invitation from {name}",
    from: "From {name} · {count} of {max} members",
    timer: "{seconds} s left to answer",
    decline: "Decline",
    accept: "Accept",
    waiting: "{count} more waiting",
    expired: "The party invitation from {name} expired.",
    switchAccept: "Switch and accept",
    needFlag: "To accept, you'll need to Flag",
    needUnflag: "To accept, you'll need to Unflag",
    unknown: "The party's flag isn't known yet.",
    switchCancelled: "Joining cancels the switch in progress.",
    switchNotSent: "Your flag could not be switched. Try again.",
    switchEnded: "The invitation ended before you joined. Your flag stays as it is.",
    switchStopped: "The switch stopped, so you did not join.",
    unsafe: "Reach a safe zone to unflag and join",
    level: "PvP opens at level {level}.",
  },
  frames: {
    panel: "Party",
    leader: "Leader",
    offline: "Offline",
    elsewhere: "Elsewhere · no data",
    dead: "Dead",
    health: "{name}, health",
    colour: "{name}'s map colour",
    count: "{count}/{max}",
    countFull: "{count}/{max} · full",
    memberOpen: "{name}, party member. Open the menu",
    leaderOpen: "{name}, party leader. Open the menu",
  },
  menu: {
    label: "Player actions",
    invite: "Invite to party",
    full: "Party is full ({max})",
    leave: "Leave party",
    remove: "Remove from party",
    promote: "Make leader",
    grant: "Can invite",
    leaderOnly: "Leader only",
    offline: "Offline",
    whisper: "Whisper",
    trade: "Trade",
    card: "View player card",
    block: "Block",
    unblock: "Unblock",
    report: "Report",
    soon: "Coming soon",
    combat: "In combat",
  },
  notice: {
    joined: "{name} joined the party.",
    joinedSelf: "You joined the party.",
    left: "{name} left the party.",
    leftSelf: "You left the party.",
    removed: "{name} was removed from the party.",
    removedSelf: "You were removed from the party.",
    leader: "{name} is now the party leader.",
    leaderSelf: "You are now the party leader.",
    granted: "{name} can now invite to the party.",
    grantedSelf: "You can now invite to the party.",
    revoked: "{name} can no longer invite to the party.",
    revokedSelf: "You can no longer invite to the party.",
    declined: "{name} declined your party invitation.",
    expired: "Your party invitation to {name} expired.",
    withdrawn: "The party invitation from {name} was withdrawn.",
    offline: "{name} went offline.",
    online: "{name} is back online.",
    ended: "The party ended.",
    endedAtJoin: "Your party ended.",
    flagMismatch: "You left the party: your flag no longer matches it.",
  },
  refused: {
    combat: "That player is in combat. Try again when the fight is over.",
    combatJoin: "You cannot join a party you are fighting.",
    cannot: "{name} cannot be invited.",
    cannotAnyone: "That player cannot be invited.",
    cannotJoin: "You cannot join that party.",
    flag: "That party's flag is not yours. Switch, then accept again.",
    level: "That party is Flagged. PvP opens at level {level}.",
    full: "That party is full ({max}).",
    rate: "You are sending party requests too quickly. Try again in {seconds} s.",
    recent: "You invited that player a moment ago. Try again in {time}.",
    busy: "That player has too many invitations waiting. Try again in {time}.",
    muted: "You are muted, so you cannot invite anyone.",
    leaderInvite: "Only the party leader, or a member the leader allows, can invite.",
    leaderOnly: "Only the party leader can do that.",
    self: "You cannot invite yourself.",
    member: "{name} is already in a party.",
    memberAnyone: "That player is already in a party.",
    pending: "Your party has already invited that player.",
    absent: "No one of that name is in your party.",
    offline: "That member is offline.",
    alone: "You are not in a party.",
    gone: "That invitation is no longer open.",
    unavailable: "Parties are not available just now. Try again in a moment.",
    unknown: "That party request did not work.",
  },
  command: {
    name: "Name a character by its first and family names, as in /invite First Family.",
  },
} as const;
